/**
 * Device configuration view — schema-driven unit configuration stage.
 *
 * Rendered inside SiteDashboardLayout so the dashboard rail, live site data,
 * and command affordances remain constant while configuring the device.
 *
 * Contains:
 * - DeviceConfigurationView — route child component (default export)
 */
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeviceConfigurationForm from '@sophocles/views/sites/components/DeviceConfiguration/DeviceConfigurationForm';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

function DeviceConfigurationView() {
  const { t } = useTranslation();
  const { siteId, selectedStructure, unit, commandLocked } = useSiteDashboardLayoutContext();
  // TODO(odl-app-contract): relay_controller.id fallback on selectedStructure not yet on the snapshot when unit is null
  const relayControllerId = unit?.id ?? selectedStructure.getIn(['relay_controller', 'id'], null);

  const deviceFormikRef = useRef(null);
  const [deviceDirty, setDeviceDirty] = useState(false);
  const [deviceCommandRequired, setDeviceCommandRequired] = useState(false);

  const handleSave = () => {
    if (deviceDirty) deviceFormikRef.current?.submitForm();
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          mt: { xs: 'var(--space-3)', md: 0 },
          p: { xs: 'var(--space-3)', md: 'var(--space-4)' },
          borderRadius: 'var(--radius-lg)',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 'var(--space-3)' }}>
          {t('deviceConfig.title', 'Device Configuration')}
        </Typography>
        <DeviceConfigurationForm
          siteId={siteId}
          relayControllerId={relayControllerId}
          hideSaveButton
          cicHasRadio={false}
          formikRef={deviceFormikRef}
          onDirtyChange={setDeviceDirty}
          onCommandRequiredChange={setDeviceCommandRequired}
        />
      </Paper>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 'var(--space-3)' }}>
        <Button
          variant="contained"
          disabled={!deviceDirty || (commandLocked && deviceCommandRequired)}
          onClick={handleSave}
        >
          {t('actions.save', 'Save')}
        </Button>
      </Stack>
    </>
  );
}

export default DeviceConfigurationView;
